<template>
  <label v-if="iconOnly" class="iconBtn" :class="{ disabled: busy }" :title="busy ? 'Uploading…' : 'Add companies from CSV'">
    <input type="file" accept=".csv" class="csvFileInput" @change="onFileSelected" :disabled="busy" />
    <AppIcon name="import_csv" :size="16" />
  </label>
  <label v-else class="btn secondary" :class="{ disabled: busy }">
    <input type="file" accept=".csv" class="csvFileInput" @change="onFileSelected" :disabled="busy" />
    <span>{{ busy ? 'Uploading…' : 'Add companies from CSV' }}</span>
  </label>
  <p v-if="notice" class="csvNotice">{{ notice }}</p>

  <Teleport to="body">
    <Modal v-if="preview" title="Confirm CSV Import" @close="cancel">
      <div class="csvPreviewBody">
        <p class="csvPreviewIntro">The following changes will be applied:</p>
        <div v-if="preview.unique_domains === 0" class="csvPreviewMuted">
          No accounts found in CSV.
        </div>
        <template v-else>
          <div class="csvPreviewSection">
            <div v-if="preview.new_to_play > 0">
              <strong>{{ preview.new_to_play }}</strong> new account{{ preview.new_to_play !== 1 ? 's' : '' }} will be added to this play
            </div>
            <div v-if="preview.already_in_play > 0" class="csvPreviewMuted">
              {{ preview.already_in_play }} account{{ preview.already_in_play !== 1 ? 's are' : ' is' }} already a member of this play (will be skipped)
            </div>
            <div v-if="preview.new_accounts_missing_name > 0" class="csvPreviewWarn">
              {{ preview.new_accounts_missing_name }} new account{{ preview.new_accounts_missing_name !== 1 ? 's are' : ' is' }} missing a name and will be imported without one.
            </div>
          </div>
          <div v-if="preview.new_column_labels?.length > 0" class="csvPreviewSection">
            <div><strong>{{ preview.new_column_labels.length }}</strong> new column{{ preview.new_column_labels.length !== 1 ? 's' : '' }} will be created:</div>
            <div class="csvPreviewChips">
              <span v-for="label in preview.new_column_labels" :key="label" class="csvPreviewBadge csvPreviewBadgeNew">{{ label }}</span>
            </div>
          </div>
          <div v-if="preview.updated_column_labels?.length > 0" class="csvPreviewSection">
            <div><strong>{{ preview.updated_column_labels.length }}</strong> existing column{{ preview.updated_column_labels.length !== 1 ? 's' : '' }} will be populated:</div>
            <div class="csvPreviewChips">
              <span v-for="label in preview.updated_column_labels" :key="label" class="csvPreviewBadge">{{ label }}</span>
            </div>
          </div>
        </template>
        <div v-if="preview.skipped_no_domain > 0" class="csvPreviewWarn">
          {{ preview.skipped_no_domain }} row{{ preview.skipped_no_domain !== 1 ? 's' : '' }} will be skipped — no valid domain or website found.
        </div>
      </div>
      <template #footer>
        <button class="btn secondary" @click="cancel">Cancel</button>
        <button class="btn primary" :disabled="busy" @click="commit">
          {{ busy ? 'Importing…' : 'Import' }}
        </button>
      </template>
    </Modal>
  </Teleport>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import Modal from './Modal.vue'
import AppIcon from '../../components/AppIcon.vue'
import { apiFetch } from '../../services/api'

const props = defineProps<{
  playId: string
  refreshPlay: () => Promise<void>
  iconOnly?: boolean
}>()

const busy = ref(false)
const notice = ref('')
const preview = ref<any>(null)
const pendingFile = ref<File | null>(null)

async function onFileSelected(evt: Event) {
  const input = evt.target as HTMLInputElement
  const file = input.files?.[0]
  input.value = ''
  if (!file) return
  busy.value = true
  preview.value = null
  try {
    const fd = new FormData()
    fd.append('file', file)
    const resp: any = await apiFetch(
      `/sales-plays/${encodeURIComponent(props.playId)}/accounts/import_csv/preview`,
      { method: 'POST', body: fd },
    )
    pendingFile.value = file
    preview.value = resp
  } catch (e: any) {
    notice.value = e?.message || 'CSV preview failed'
  } finally {
    busy.value = false
  }
}

function cancel() {
  preview.value = null
  pendingFile.value = null
}

async function commit() {
  if (!pendingFile.value) return
  busy.value = true
  try {
    const fd = new FormData()
    fd.append('file', pendingFile.value)
    const resp: any = await apiFetch(
      `/sales-plays/${encodeURIComponent(props.playId)}/accounts/import_csv`,
      { method: 'POST', body: fd },
    )
    const staged = Number(resp?.newly_staged_count ?? 0)
    notice.value = `Imported ${staged} candidate(s) from CSV.`
    cancel()
    await props.refreshPlay()
  } catch (e: any) {
    notice.value = e?.message || 'CSV import failed'
  } finally {
    busy.value = false
  }
}
</script>

<style scoped>
.csvFileInput { display: none; }
.disabled { opacity: 0.5; cursor: not-allowed; }
.iconBtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 1px solid transparent;
  border-radius: var(--radius-md);
  background: none;
  color: var(--color-text-secondary);
  cursor: pointer;
}
.iconBtn:hover { color: var(--color-text-primary); background: var(--color-bg-subtle); }
.csvNotice {
  margin: 0;
  padding: var(--space-2) var(--space-3);
  background: var(--color-accent-soft);
  color: var(--color-accent);
  border-radius: var(--radius-md);
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
}
.csvPreviewBody { display: flex; flex-direction: column; gap: var(--space-4); font-family: var(--font-sans); font-size: var(--fs-sm); color: var(--color-text-primary); line-height: var(--lh-normal); }
.csvPreviewIntro { margin: 0; color: var(--color-text-secondary); }
.csvPreviewSection { display: flex; flex-direction: column; gap: var(--space-1); }
.csvPreviewMuted { color: var(--color-text-tertiary); }
.csvPreviewWarn { color: var(--color-warning, #c2410c); }
.csvPreviewChips { display: flex; flex-wrap: wrap; gap: var(--space-1); }
.csvPreviewBadge { display: inline-block; padding: 2px var(--space-2); border-radius: var(--radius-full); font-size: var(--fs-xs); background: var(--color-surface-subtle, #f3f4f6); color: var(--color-text-secondary); }
.csvPreviewBadgeNew { background: var(--color-accent-soft); color: var(--color-accent); }
</style>
